import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next'
import {useInView} from 'framer-motion'
import {AnimatedListSliceWithContainer} from 'prismicio-types'
import {FC, useEffect, useRef, useState} from 'react'
import {Container} from 'src/common/Container'
import {sanitizeHtml} from 'src/common/helpers/sanitizeHtml'
import cn from 'classnames'
import {ContainerSideText} from './components/ContainerSideText'

type AnimatedListDefaultProps = {
  slice: AnimatedListSliceWithContainer & {
    slice_type: string
    variation: string
  }
}

export const AnimatedListContainerVariation: FC<AnimatedListDefaultProps> = ({slice}) => {
  const sanitizedTitle = sanitizeHtml(slice.primary.title!)
  const containerRef = useRef(null)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const [activeStyle, setActiveStyle] = useState(0)
  const listItems = slice.primary.elements.length
  const isInView = useInView(containerRef, {amount: 0.5, once: false})
  const animationDuration = slice.primary.animation_duration || 5000

  // Start animation when the component is in view
  const startInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }

    intervalRef.current = setInterval(() => {
      setActiveStyle((prev) => {
        if (prev + 1 < listItems) {
          return prev + 1
        } else {
          return 0
        }
      })
    }, animationDuration)
  }

  const handleItemClick = (index: number) => {
    setActiveStyle(index)
    startInterval() // Restart the interval
  }

  // Start/stop interval based on component visibility
  useEffect(() => {
    if (!isInView) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }

      return
    }

    startInterval()

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- not need to add listItems dependency
  }, [isInView])

  return (
    <Container
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative justify-center !py-10 px-5 md:px-10 md:py-20 lg:px-5 xl:px-0"
      containerRef={containerRef}
    >
      <div className="relative isolate h-full w-full max-w-[1200px] overflow-hidden rounded-lg p-6 md:p-12">
        {isFilled.imageThumbnail(slice.primary.background_image.Mobile) && (
          <PrismicNextImage
            quality={100}
            field={slice.primary.background_image.Mobile}
            className="mmd:hidden absolute left-0 top-0 -z-10 h-full w-full object-cover"
          />
        )}
        <PrismicNextImage
          quality={100}
          field={slice.primary.background_image}
          className="absolute left-0 top-0 -z-10 h-full w-full object-cover max-md:hidden md:max-lg:object-left"
        />
        <div className="flex h-[640px] flex-col justify-between md:h-[640px]">
          <h2 className="text-tg-h3 text-moss-white" dangerouslySetInnerHTML={{__html: sanitizedTitle}} />
          <div className="flex w-full flex-col">
            <div className="flex flex-col gap-6 lg:flex-row lg:justify-between">
              <ul className="flex flex-col gap-[11px]">
                {slice.primary.elements.map((element, index) => (
                  <li key={index}>
                    <h3
                      className={cn(
                        'w-full cursor-pointer text-[clamp(48px,5vw,64px)] font-normal leading-12 tracking-[-.48px] transition-colors duration-300 hover:text-moss-grey-150 hover:!delay-0 hover:!duration-200 lg:w-[70%] lg:max-w-[835px] lg:leading-[64px] lg:tracking-[-.64px]',
                        {
                          'text-moss-white': activeStyle === index,
                          'text-moss-grey-200': activeStyle !== index,
                        },
                      )}
                      onClick={() => handleItemClick(index)}
                    >
                      {element.text}
                    </h3>
                  </li>
                ))}
              </ul>
              <ContainerSideText activeStyle={activeStyle} content={slice.primary.elements[activeStyle].side_text} />
            </div>
          </div>
        </div>
      </div>
    </Container>
  )
}
